'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { ZERO } from '@morpho-org/dapp-core';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { TotalMyBorrowError, TotalMyBorrowLoading } from '@/components/BorrowPage/BorrowDashboard/DashboardCard/ChartMyBorrow/TotalMyBorrow/states';
import { useDashboardContext } from '@/contexts/Dashboard/Dashboard.context';
import { getParsedValue } from '@/helpers/formatters';
import { splitValueAndUnit } from '@/helpers/number';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { useDashboardBorrowPositions } from '../hooks/useDashboardBorrowPositions';
interface DashboardBorrowHeroTotalBorrowProps {
  chainIds: ChainId[];
}
const DashboardBorrowHeroTotalBorrow = t0 => {
  const $ = _c(32);
  const {
    chainIds
  } = t0;
  const {
    address
  } = useViewedAccountAddress();
  const {
    borrowDashboardMode
  } = useDashboardContext();
  const isMobile = useIsMobile();
  const {
    borrowPositions,
    loading,
    isError
  } = useDashboardBorrowPositions(address);
  let t1;
  if ($[0] !== chainIds) {
    t1 = new Set(chainIds);
    $[0] = chainIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedChains = t1;
  let formattedValueAndUnit;
  let t2;
  if ($[2] !== borrowDashboardMode || $[3] !== borrowPositions || $[4] !== selectedChains) {
    let t3;
    if ($[7] !== selectedChains) {
      t3 = position => selectedChains.has(position.market.chainId);
      $[7] = selectedChains;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    const positions = borrowPositions.filter(t3);
    const value = positions.reduce(_temp, 0);
    const borrowAssetsUsd = getParsedValue(value, safeParseNumber);
    const value_0 = positions.reduce(_temp2, 0);
    const collateralUsd = getParsedValue(value_0, safeParseNumber);
    const _value = borrowDashboardMode === "loan" ? borrowAssetsUsd : collateralUsd;
    const formatted = formatters.short.default("0.00").digits(2).of(_value ?? ZERO, 18);
    formattedValueAndUnit = splitValueAndUnit(formatted);
    const selectedUsdValue = borrowDashboardMode === "loan" ? borrowAssetsUsd : collateralUsd;
    t2 = formatters.commas.unit("$").digits(2).default("$0.00").of(selectedUsdValue, 18);
    $[2] = borrowDashboardMode;
    $[3] = borrowPositions;
    $[4] = selectedChains;
    $[5] = formattedValueAndUnit;
    $[6] = t2;
  } else {
    formattedValueAndUnit = $[5];
    t2 = $[6];
  }
  const detailedFormattedValue = t2;
  if (loading) {
    let t3;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TotalMyBorrowLoading />;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (isError) {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TotalMyBorrowError />;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (isMobile) {
    let t3;
    if ($[11] !== formattedValueAndUnit.value) {
      t3 = <span className="text-title-sm text-foreground">${formattedValueAndUnit.value}</span>;
      $[11] = formattedValueAndUnit.value;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== formattedValueAndUnit.unit) {
      t4 = <span className="text-title-sm text-secondary-foreground">{formattedValueAndUnit.unit}</span>;
      $[13] = formattedValueAndUnit.unit;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t3 || $[16] !== t4) {
      t5 = <TooltipTrigger asChild={true}><div className="flex w-[fit-content] items-center">{t3}{t4}</div></TooltipTrigger>;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] !== detailedFormattedValue) {
      t6 = !!detailedFormattedValue && <TooltipContent>{detailedFormattedValue}</TooltipContent>;
      $[18] = detailedFormattedValue;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== t5 || $[21] !== t6) {
      t7 = <Tooltip>{t5}{t6}</Tooltip>;
      $[20] = t5;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t3;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-title-md text-muted-foreground">$</span>;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== formattedValueAndUnit.unit || $[25] !== formattedValueAndUnit.value) {
    t4 = <TooltipTrigger asChild={true}><div className="flex items-center gap-[2px]">{t3}<span className="text-title-md text-foreground">{formattedValueAndUnit.value}{formattedValueAndUnit.unit}</span></div></TooltipTrigger>;
    $[24] = formattedValueAndUnit.unit;
    $[25] = formattedValueAndUnit.value;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  let t5;
  if ($[27] !== detailedFormattedValue) {
    t5 = !!detailedFormattedValue && <TooltipContent side="right">{detailedFormattedValue}</TooltipContent>;
    $[27] = detailedFormattedValue;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== t4 || $[30] !== t5) {
    t6 = <Tooltip>{t4}{t5}</Tooltip>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
};
export default DashboardBorrowHeroTotalBorrow;
function _temp(acc, position_0) {
  return acc + (position_0.borrowAssetsUsd ?? 0);
}
function _temp2(acc_0, position_1) {
  return acc_0 + (position_1.collateralUsd ?? 0);
}